import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { formatPercentage, calculateMetricsFromTasks } from "@/lib/utils";
import type { Metrics, Task } from "@/types/dashboard";
import { chart } from "@/lib/brand";
import { ChartEmpty, ChartHeader } from "@/components/charts/ChartShell";

interface SuccessFailurePieProps {
  metrics: Metrics | null;
  tasks: Record<string, Task>;
  type?: "score" | "sql";
  totalQuestions?: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="chart-tooltip">
        <p className="flex items-center gap-2 font-semibold text-foreground">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: data.payload.color }} />
          {data.name}
        </p>
        <p className="text-muted-foreground">
          <span className="font-semibold tabular-nums text-foreground">{data.value}</span> ({formatPercentage(data.payload.percentage)})
        </p>
      </div>
    );
  }
  return null;
};

export function SuccessFailurePie({ metrics: propMetrics, tasks, type = "score", totalQuestions }: SuccessFailurePieProps) {
  // Calculate metrics from tasks (single source of truth)
  const metrics = useMemo(() => {
    if (tasks && Object.keys(tasks).length > 0) {
      return calculateMetricsFromTasks(tasks, totalQuestions);
    }
    return propMetrics;
  }, [tasks, totalQuestions, propMetrics]);

  const chartData = useMemo(() => {
    if (!metrics) return [];

    if (type === "sql") {
      // SQL Correctness Pie Chart
      const completed = metrics.completed;
      const failed = metrics.failed;
      const pending = metrics.queued + metrics.running;
      const total = completed + failed + pending;

      if (total === 0) return [];

      const sqlValid = (metrics.sql_success_rate / 100) * (completed + failed);
      const sqlInvalid = (completed + failed) - sqlValid;

      const data: { name: string; value: number; percentage: number; color: string }[] = [
        { name: "SQL geldig", value: Math.round(sqlValid), percentage: (sqlValid / total) * 100, color: chart.success },
        { name: "SQL ongeldig", value: Math.round(sqlInvalid), percentage: (sqlInvalid / total) * 100, color: chart.failure },
      ];

      if (pending > 0) {
        data.push({ name: "In behandeling", value: pending, percentage: (pending / total) * 100, color: chart.neutral });
      }

      return data.filter(item => item.value > 0);
    } else {
      // Score Distribution Pie Chart
      const scores = metrics.scores || [];
      const completed = scores.length;
      const pending = metrics.queued + metrics.running;
      const total = completed + pending;

      if (total === 0) return [];

      const excellent = scores.filter(s => s >= 4.5).length;
      const good = scores.filter(s => s >= 3.5 && s < 4.5).length;
      const fair = scores.filter(s => s >= 2.5 && s < 3.5).length;
      const poor = scores.filter(s => s < 2.5).length;

      const data: { name: string; value: number; percentage: number; color: string }[] = [
        { name: "Uitstekend (4,5–5,0)", value: excellent, percentage: (excellent / total) * 100, color: chart.scores[4] },
        { name: "Goed (3,5–4,5)", value: good, percentage: (good / total) * 100, color: chart.scores[3] },
        { name: "Matig (2,5–3,5)", value: fair, percentage: (fair / total) * 100, color: chart.scores[2] },
        { name: "Onvoldoende (< 2,5)", value: poor, percentage: (poor / total) * 100, color: chart.scores[0] },
      ];

      if (pending > 0) {
        data.push({ name: "In behandeling", value: pending, percentage: (pending / total) * 100, color: chart.neutral });
      }

      return data.filter(item => item.value > 0);
    }
  }, [metrics, type]);

  const title = type === "sql" ? "SQL geldig" : "Scoreverdeling";

  if (!metrics || chartData.length === 0) {
    return <ChartEmpty title={title} />;
  }

  const subtitle = type === "sql"
    ? `${formatPercentage(metrics.sql_success_rate)} geldige SQL`
    : `${metrics.scores.length} beoordeelde ${metrics.scores.length === 1 ? "vraag" : "vragen"}`;

  return (
    <div className="flex h-full w-full flex-col">
      <ChartHeader title={title} subtitle={subtitle} />

      <div className="min-h-0 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="45%"
              labelLine={false}
              label={({ percent }) => { const percentage = (percent ?? 0) * 100; return percentage > 5 ? `${formatPercentage(percentage, 0)}` : ''; }}
              innerRadius="42%"
              outerRadius="70%"
              paddingAngle={1}
              dataKey="value"
              stroke="#FFFFFF"
              strokeWidth={2}
              fill={chart.neutral}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              itemSorter={null}
              verticalAlign="bottom"
              height={type === "sql" ? 30 : 60}
              iconType="circle"
              iconSize={9}
              wrapperStyle={{ fontSize: '12px' }}
              formatter={(value) => <span style={{ color: chart.axisText }}>{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
